import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  noIndex?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = 'https://cloudsibyl.com/CloudSibyl-logo.png',
  noIndex = false
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const baseUrl = 'https://cloudsibyl.com';
  const currentUrl = canonicalUrl || `${baseUrl}${window.location.pathname}`;
  
  // Default values
  const defaultTitle = currentLanguage === 'fr'
    ? 'CloudSibyl — Flowry et FinOps infonuagique'
    : 'CloudSibyl — Flowry and Cloud FinOps';

  const defaultDescription = currentLanguage === 'fr'
    ? 'Des intégrations MCP personnalisées et une automatisation des processus d\'affaires par l\'IA, axée sur le ROI, avec Flowry, aux côtés de CloudSibyl FinOps.'
    : 'Customized MCP integrations and ROI-first AI business process automation with Flowry, alongside CloudSibyl FinOps.';
  
  const defaultKeywords = currentLanguage === 'fr'
    ? 'CloudSibyl, gestion des coûts cloud, empreinte carbone, AWS, Azure, Google Cloud, services cloud, FinOps, durabilité, optimisation cloud'
    : 'CloudSibyl, cloud cost management, carbon footprint, AWS, Azure, Google Cloud, cloud services, FinOps, sustainability, cloud optimization';

  const pageTitle = title ? `${title} | CloudSibyl` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;

  // Hreflang URLs
  const enUrl = currentUrl.replace('/fr/', '/').replace('/fr', '');
  const frUrl = currentUrl.includes('/fr/') ? currentUrl : currentUrl.replace(baseUrl, `${baseUrl}/fr`);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="language" content={currentLanguage === 'fr' ? 'French' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Hreflang for multilingual support */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="fr" href={frUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CloudSibyl" />
      <meta property="og:locale" content={currentLanguage === 'fr' ? 'fr_CA' : 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
    </Helmet>
  );
};

export default SEO;
